import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";

/**
 * Beyond Sure — Combobox / Autocomplete primitive (A1, portal).
 *
 * A text input bound to a filtered, keyboard-navigable suggestion list.
 * Use for: Family add-policy insurer ("TATA AIG, HDFC Ergo, ICICI…"),
 * plan suggestions, relation suggestions ("Spouse, Child, Parent…"),
 * the Settings language picker.
 *
 * Keyboard:
 *   ↑/↓     move highlight through suggestions
 *   Enter   pick the highlighted option
 *   Tab     pick + leave the field
 *   Esc     close the list (input keeps focus)
 *
 * ARIA:
 *   role="combobox" + aria-expanded + aria-controls on the input
 *   role="listbox" on the dropdown, role="option" per item
 *   aria-activedescendant tracks the highlighted option
 *
 * Items are simple {value, label, hint?} objects (the consumer can map
 * any data shape into this). Pass `allowCreate` to enable a "Create
 * X" affordance at the bottom of the list when no exact match exists.
 */

export interface ComboboxOption {
  value: string;
  label: string;
  /** Optional secondary text shown smaller below the label. */
  hint?: string;
}

interface ComboboxProps {
  /** Current text value (controlled). */
  value: string;
  onChange: (next: string) => void;
  /** Called when the user picks an option (mouse or Enter). */
  onSelect?: (option: ComboboxOption) => void;
  /** The candidate options. Caller controls filtering OR uses the
   *  built-in filter via `filter: "builtin"` (default — case-insensitive
   *  substring on label). For async data, set filter to "none" and pass
   *  already-filtered options. */
  options: ComboboxOption[];
  filter?: "builtin" | "none";
  /** Allow free-text creation. When the typed value doesn't match an
   *  existing option, a "Create '<value>'" row appears at the bottom. */
  allowCreate?: boolean;
  onCreate?: (newValue: string) => void;
  placeholder?: string;
  id?: string;
  /** Optional disabled state. */
  disabled?: boolean;
  /** Hide the input chrome — useful inside a Field wrapper. */
  className?: string;
  /** ARIA label when no visible <label> is attached. */
  "aria-label"?: string;
  /** data-testid forwarded to the input. */
  "data-testid"?: string;
}

export function Combobox({
  value,
  onChange,
  onSelect,
  options,
  filter = "builtin",
  allowCreate = false,
  onCreate,
  placeholder,
  id,
  disabled,
  className,
  "aria-label": ariaLabel,
  "data-testid": testid,
}: ComboboxProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const listId = `${inputId}-list`;

  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const reducedMotion = useReducedMotion();
  const [position, setPosition] = useState<{ top: number; left: number; width: number }>(
    { top: 0, left: 0, width: 0 },
  );

  const filtered = useMemo(() => {
    if (filter === "none") return options;
    const q = value.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, value, filter]);

  const exactMatch = useMemo(
    () => options.some((o) => o.label.trim().toLowerCase() === value.trim().toLowerCase()),
    [options, value],
  );

  const showCreate = allowCreate && value.trim().length > 0 && !exactMatch;
  const totalRows = filtered.length + (showCreate ? 1 : 0);

  /* ── Position the dropdown under the input ─────────────────────── */
  const recompute = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPosition({
      top: r.bottom + 4,
      left: r.left,
      width: r.width,
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    recompute();
    const onScroll = () => recompute();
    const onResize = () => recompute();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open, recompute]);

  /* ── Click outside closes ──────────────────────────────────────── */
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (inputRef.current?.contains(target)) return;
      if (listRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  /* ── Reset highlight when filter changes ───────────────────────── */
  useEffect(() => {
    setActiveIdx(0);
  }, [value, filtered.length]);

  /* ── Keyboard nav ──────────────────────────────────────────────── */
  function pick(idx: number): void {
    if (idx < filtered.length) {
      const o = filtered[idx];
      onChange(o.label);
      onSelect?.(o);
      setOpen(false);
    } else if (showCreate) {
      onCreate?.(value.trim());
      setOpen(false);
    }
    requestAnimationFrame(() => inputRef.current?.focus());
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) setOpen(true);
      else setActiveIdx((i) => (i < totalRows - 1 ? i + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => (i > 0 ? i - 1 : Math.max(0, totalRows - 1)));
    } else if (e.key === "Enter") {
      if (open && totalRows > 0) {
        e.preventDefault();
        pick(activeIdx);
      }
    } else if (e.key === "Tab") {
      if (open && totalRows > 0) pick(activeIdx);
    } else if (e.key === "Escape") {
      if (open) {
        e.stopPropagation();
        setOpen(false);
      }
    }
  }

  const activeOptionId = open && totalRows > 0 ? `${listId}-opt-${activeIdx}` : undefined;
  const portalRoot = typeof document !== "undefined" ? document.body : null;

  return (
    <>
      <input
        ref={inputRef}
        id={inputId}
        type="text"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          if (!open) setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        spellCheck={false}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={activeOptionId}
        aria-autocomplete="list"
        aria-label={ariaLabel}
        data-testid={testid}
        className={[
          "w-full h-9 px-3 rounded-[10px]",
          "border border-[var(--color-ink-08)]",
          "bg-[var(--color-paper)] text-[var(--color-ink)]",
          "text-[13.5px] leading-[1.4]",
          "transition-colors duration-[var(--duration-fast)]",
          "focus:outline-none focus:border-[var(--color-brand-deep)] focus:shadow-[0_0_0_3px_rgb(20_75_51_/_0.08)]",
          "disabled:opacity-40 disabled:cursor-not-allowed",
          className ?? "",
        ].join(" ")}
      />
      {portalRoot
        ? createPortal(
            <AnimatePresence>
              {open && totalRows > 0 ? (
                <motion.div
                  ref={listRef}
                  id={listId}
                  role="listbox"
                  initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -4 }}
                  transition={{ duration: 0.14 }}
                  style={{
                    position: "fixed",
                    top: position.top,
                    left: position.left,
                    width: Math.max(position.width, 200),
                    zIndex: 1000,
                  }}
                  className={[
                    "rounded-[12px] overflow-hidden max-h-[280px] overflow-y-auto",
                    "bg-[var(--color-paper)]",
                    "border border-[var(--color-ink-08)]",
                    "shadow-[0_8px_32px_-12px_rgba(0,0,0,0.18)]",
                  ].join(" ")}
                >
                  {filtered.map((opt, i) => (
                    <div
                      key={opt.value}
                      id={`${listId}-opt-${i}`}
                      role="option"
                      aria-selected={i === activeIdx}
                      onMouseEnter={() => setActiveIdx(i)}
                      onMouseDown={(e) => {
                        // mousedown (not click) so the input doesn't lose
                        // focus + close the list before the click fires.
                        e.preventDefault();
                        pick(i);
                      }}
                      className={[
                        "flex flex-col gap-0.5 px-3 py-2 cursor-pointer",
                        "text-[13.5px] leading-[1.3]",
                        i === activeIdx
                          ? "bg-[var(--color-brand-soft)] text-[var(--color-ink)]"
                          : "text-[var(--color-ink)] hover:bg-[var(--color-ink-04)]",
                      ].join(" ")}
                    >
                      <span className="font-medium truncate">{opt.label}</span>
                      {opt.hint ? (
                        <span className="text-[11.5px] text-[var(--color-ink-60)] truncate">
                          {opt.hint}
                        </span>
                      ) : null}
                    </div>
                  ))}
                  {showCreate ? (
                    <div
                      id={`${listId}-opt-${filtered.length}`}
                      role="option"
                      aria-selected={activeIdx === filtered.length}
                      onMouseEnter={() => setActiveIdx(filtered.length)}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        pick(filtered.length);
                      }}
                      className={[
                        "flex items-center gap-2 px-3 py-2 cursor-pointer border-t border-[var(--color-ink-08)]",
                        "text-[13.5px] leading-[1.3]",
                        activeIdx === filtered.length
                          ? "bg-[var(--color-brand-soft)]"
                          : "hover:bg-[var(--color-ink-04)]",
                      ].join(" ")}
                    >
                      <span className="text-[var(--color-brand-deep)]" aria-hidden>
                        +
                      </span>
                      <span className="text-[var(--color-ink)]">
                        Create &ldquo;<span className="font-medium">{value.trim()}</span>&rdquo;
                      </span>
                    </div>
                  ) : null}
                </motion.div>
              ) : null}
            </AnimatePresence>,
            portalRoot,
          )
        : null}
    </>
  );
}

export default Combobox;
